import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Mail,
  Server,
  Send,
  CheckCircle,
  AlertCircle,
  Loader2,
  Save,
  TestTube,
  Clock,
  CalendarClock,
  Plus,
  X
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [savingSummary, setSavingSummary] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);
  const [newRecipient, setNewRecipient] = useState("");

  const [smtpForm, setSmtpForm] = useState({
    smtp_host: "",
    smtp_port: 587,
    smtp_user: "",
    smtp_password: "",
    smtp_from_email: "",
    smtp_from_name: "ServeurOnline",
    smtp_use_tls: true,
    alert_email: "",
    enabled: true
  });

  const [summaryForm, setSummaryForm] = useState({
    enabled: false,
    send_hour: 8,
    recipients: [],
    include_success: true,
    include_warnings: true,
    include_errors: true
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const headers = getAuthHeader();
      const [smtpRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/settings/smtp`, { headers }),
        axios.get(`${API_URL}/api/settings/daily-summary`, { headers })
      ]);
      if (smtpRes.data.configured) {
        setSmtpConfigured(true);
        setSmtpForm({
          ...smtpForm,
          ...smtpRes.data,
          smtp_password: ""
        });
      }
      if (summaryRes.data) {
        setSummaryForm(prev => ({ ...prev, ...summaryRes.data }));
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = getAuthHeader();
      
      // Validate required fields
      if (!smtpForm.smtp_host || !smtpForm.smtp_user || !smtpForm.smtp_from_email || !smtpForm.alert_email) {
        toast.error("Veuillez remplir tous les champs obligatoires");
        setSaving(false);
        return;
      }

      // If password is empty and already configured, don't send it
      const dataToSend = { ...smtpForm };
      if (!dataToSend.smtp_password && smtpConfigured) {
        delete dataToSend.smtp_password;
        await axios.put(`${API_URL}/api/settings/smtp`, dataToSend, { headers });
      } else {
        await axios.post(`${API_URL}/api/settings/smtp`, dataToSend, { headers });
      }

      toast.success("Configuration SMTP enregistree");
      setSmtpConfigured(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const headers = getAuthHeader();
      await axios.post(`${API_URL}/api/settings/smtp/test`, {}, { headers });
      toast.success("Email de test envoye avec succes !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du test");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-white">Parametres</h1>
        <p className="text-slate-400 mt-1">Configuration des alertes email et du serveur SMTP</p>
      </div>

      {/* SMTP Configuration */}
      <div className="glass-card rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-indigo-500/20">
              <Mail className="text-indigo-400" size={24} />
            </div>
            <div>
              <h2 className="font-heading font-bold text-white text-lg">Configuration SMTP</h2>
              <p className="text-sm text-slate-400">Parametres du serveur mail pour les alertes</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {smtpConfigured ? (
              <span className="flex items-center gap-1 text-sm text-emerald-400">
                <CheckCircle size={16} />
                Configure
              </span>
            ) : (
              <span className="flex items-center gap-1 text-sm text-amber-400">
                <AlertCircle size={16} />
                Non configure
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Server settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Server size={16} />
              Serveur SMTP
            </h3>

            <div className="space-y-2">
              <Label className="text-slate-400">Hote SMTP *</Label>
              <Input
                value={smtpForm.smtp_host}
                onChange={(e) => setSmtpForm({ ...smtpForm, smtp_host: e.target.value })}
                placeholder="smtp.votreserveur.com"
                className="bg-slate-950 border-slate-700 text-white"
                data-testid="smtp-host-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-400">Port</Label>
                <Input
                  type="number"
                  value={smtpForm.smtp_port}
                  onChange={(e) => setSmtpForm({ ...smtpForm, smtp_port: parseInt(e.target.value) || 587 })}
                  className="bg-slate-950 border-slate-700 text-white"
                  data-testid="smtp-port-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400">TLS</Label>
                <div className="flex items-center h-10">
                  <Switch
                    checked={smtpForm.smtp_use_tls}
                    onCheckedChange={(checked) => setSmtpForm({ ...smtpForm, smtp_use_tls: checked })}
                    data-testid="smtp-tls-switch"
                  />
                  <span className="ml-2 text-sm text-slate-400">
                    {smtpForm.smtp_use_tls ? "Active" : "Desactive"}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-400">Utilisateur SMTP *</Label>
              <Input
                value={smtpForm.smtp_user}
                onChange={(e) => setSmtpForm({ ...smtpForm, smtp_user: e.target.value })}
                placeholder="user@votreserveur.com"
                className="bg-slate-950 border-slate-700 text-white"
                data-testid="smtp-user-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-400">
                Mot de passe SMTP {smtpConfigured && "(laisser vide pour conserver)"}
              </Label>
              <Input
                type="password"
                value={smtpForm.smtp_password}
                onChange={(e) => setSmtpForm({ ...smtpForm, smtp_password: e.target.value })}
                placeholder={smtpConfigured ? "••••••••" : "Votre mot de passe"}
                className="bg-slate-950 border-slate-700 text-white"
                data-testid="smtp-password-input"
              />
            </div>
          </div>

          {/* Email settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Send size={16} />
              Parametres d&apos;envoi
            </h3>

            <div className="space-y-2">
              <Label className="text-slate-400">Email expediteur *</Label>
              <Input
                type="email"
                value={smtpForm.smtp_from_email}
                onChange={(e) => setSmtpForm({ ...smtpForm, smtp_from_email: e.target.value })}
                placeholder="alertes@votreentreprise.com"
                className="bg-slate-950 border-slate-700 text-white"
                data-testid="smtp-from-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-400">Nom expediteur</Label>
              <Input
                value={smtpForm.smtp_from_name}
                onChange={(e) => setSmtpForm({ ...smtpForm, smtp_from_name: e.target.value })}
                placeholder="ServeurOnline"
                className="bg-slate-950 border-slate-700 text-white"
                data-testid="smtp-from-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-400">Email du support technique *</Label>
              <Input
                type="email"
                value={smtpForm.alert_email}
                onChange={(e) => setSmtpForm({ ...smtpForm, alert_email: e.target.value })}
                placeholder="support@votreentreprise.com"
                className="bg-slate-950 border-slate-700 text-white"
                data-testid="alert-email-input"
              />
              <p className="text-xs text-slate-500">
                Cet email recevra toutes les alertes de sauvegarde
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-400">Alertes actives</Label>
              <div className="flex items-center h-10">
                <Switch
                  checked={smtpForm.enabled}
                  onCheckedChange={(checked) => setSmtpForm({ ...smtpForm, enabled: checked })}
                  data-testid="smtp-enabled-switch"
                />
                <span className="ml-2 text-sm text-slate-400">
                  {smtpForm.enabled ? "Alertes email activees" : "Alertes email desactivees"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4 mt-6 pt-6 border-t border-slate-700">
          <Button
            variant="secondary"
            onClick={handleTest}
            disabled={!smtpConfigured || testing}
            className="bg-slate-800 hover:bg-slate-700"
            data-testid="test-smtp-btn"
          >
            {testing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <TestTube className="mr-2 h-4 w-4" />
            )}
            Tester la configuration
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="save-smtp-btn"
          >
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Alert preferences info */}
      <div className="glass-card rounded-lg p-6">
        <h2 className="font-heading font-bold text-white text-lg mb-4">Preferences d&apos;alertes par client</h2>
        <p className="text-slate-400 mb-4">
          Vous pouvez configurer les preferences d&apos;alertes individuellement pour chaque client 
          (erreur, warning, succes) lors de la creation ou modification d&apos;un client.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
            <h3 className="font-medium text-red-400 mb-1">Alerte Erreur</h3>
            <p className="text-sm text-slate-400">Envoyee quand une sauvegarde echoue</p>
          </div>
          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <h3 className="font-medium text-amber-400 mb-1">Alerte Warning</h3>
            <p className="text-sm text-slate-400">Envoyee en cas d&apos;avertissement</p>
          </div>
          <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <h3 className="font-medium text-emerald-400 mb-1">Alerte Succes</h3>
            <p className="text-sm text-slate-400">Envoyee quand une sauvegarde reussit</p>
          </div>
        </div>
      </div>

      {/* Daily Summary */}
      <div className="glass-card rounded-lg p-6" data-testid="daily-summary-section">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-amber-500/20">
              <CalendarClock className="text-amber-400" size={24} />
            </div>
            <div>
              <h2 className="font-heading font-bold text-white text-lg">Resume quotidien</h2>
              <p className="text-sm text-slate-400">Recevez un email recapitulatif chaque jour</p>
            </div>
          </div>
          <Switch
            checked={summaryForm.enabled}
            onCheckedChange={(checked) => setSummaryForm({ ...summaryForm, enabled: checked })}
            data-testid="summary-enabled-switch"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-400">Heure d'envoi (UTC)</Label>
              <select
                value={summaryForm.send_hour}
                onChange={(e) => setSummaryForm({ ...summaryForm, send_hour: parseInt(e.target.value) })}
                className="w-full bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-2 text-sm"
                data-testid="summary-hour-select"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, "0")}:00 UTC</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-400">Contenu du resume</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={summaryForm.include_errors} onChange={(e) => setSummaryForm({ ...summaryForm, include_errors: e.target.checked })} className="rounded border-slate-700" />
                  Inclure les erreurs
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={summaryForm.include_warnings} onChange={(e) => setSummaryForm({ ...summaryForm, include_warnings: e.target.checked })} className="rounded border-slate-700" />
                  Inclure les avertissements
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={summaryForm.include_success} onChange={(e) => setSummaryForm({ ...summaryForm, include_success: e.target.checked })} className="rounded border-slate-700" />
                  Inclure les succes
                </label>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-400">Destinataires</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={newRecipient}
                  onChange={(e) => setNewRecipient(e.target.value)}
                  placeholder="email@exemple.com"
                  className="bg-slate-950 border-slate-700 text-white"
                  data-testid="summary-recipient-input"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newRecipient.trim()) {
                      e.preventDefault();
                      if (!summaryForm.recipients.includes(newRecipient.trim())) {
                        setSummaryForm({ ...summaryForm, recipients: [...summaryForm.recipients, newRecipient.trim()] });
                      }
                      setNewRecipient("");
                    }
                  }}
                />
                <Button
                  size="icon"
                  variant="secondary"
                  className="bg-slate-800 hover:bg-slate-700 shrink-0"
                  onClick={() => {
                    if (newRecipient.trim() && !summaryForm.recipients.includes(newRecipient.trim())) {
                      setSummaryForm({ ...summaryForm, recipients: [...summaryForm.recipients, newRecipient.trim()] });
                      setNewRecipient("");
                    }
                  }}
                  data-testid="add-recipient-btn"
                >
                  <Plus size={16} />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {summaryForm.recipients.map((r, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-slate-800 text-slate-300 text-xs px-2.5 py-1 rounded-full">
                    {r}
                    <button onClick={() => setSummaryForm({ ...summaryForm, recipients: summaryForm.recipients.filter((_, idx) => idx !== i) })} className="text-slate-500 hover:text-white">
                      <X size={12} />
                    </button>
                  </span>
                ))}
                {summaryForm.recipients.length === 0 && (
                  <span className="text-xs text-slate-600">Votre email sera utilise par defaut</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-4 mt-6 pt-6 border-t border-slate-700">
          <Button
            variant="secondary"
            onClick={async () => {
              setSendingSummary(true);
              try {
                const headers = getAuthHeader();
                const res = await axios.post(`${API_URL}/api/settings/daily-summary/send-now`, {}, { headers });
                toast.success(`Resume envoye a ${res.data.recipients?.join(", ")}`);
              } catch (error) {
                toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
              } finally {
                setSendingSummary(false);
              }
            }}
            disabled={!smtpConfigured || sendingSummary}
            className="bg-slate-800 hover:bg-slate-700"
            data-testid="send-summary-now-btn"
          >
            {sendingSummary ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Envoyer maintenant
          </Button>
          <Button
            onClick={async () => {
              setSavingSummary(true);
              try {
                const headers = getAuthHeader();
                await axios.post(`${API_URL}/api/settings/daily-summary`, summaryForm, { headers });
                toast.success("Configuration du resume enregistree");
              } catch (error) {
                toast.error("Erreur lors de l'enregistrement");
              } finally {
                setSavingSummary(false);
              }
            }}
            disabled={savingSummary}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="save-summary-btn"
          >
            {savingSummary ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
